/**
 * NOTE: both CONSUMER_API_HOST and CL_ENDPOINTS_URL need a fallback value because
 * CircleCI is failing to load values into process.env and causing tests to fail.
 * We'll need ProdOps help to address this and clean things up
 */
const CONSUMER_API_HOST =
  process.env.VUE_APP_PAYJOY_API_HOST || "https://vm.corp.payjoy.com";
const CONSUMER_HOST =
  process.env.VUE_APP_PAYJOY_HOST || "https://vm.corp.payjoy.com";
const CONSUMER_API_BASE_URL = `${CONSUMER_API_HOST}/consumer/creditline`;
const CL_ENDPOINTS_URL =
  process.env.VUE_APP_PAYJOY_API_URL || "https://vm.corp.payjoy.com/api";
const CL_V1_API_URL = `${CL_ENDPOINTS_URL}/credit-line/v1`;

// Define the type for any string-based dictionary
type ApiUrlDictionary = {
  [K in string]: string;
};

// Create a const object with all URLs
const creditLineApiUrls: ApiUrlDictionary = {
  // ------ Consumer API migrated endpoints START ------
  GET_COUNTRY: `${CONSUMER_API_BASE_URL}/get-country`,
  WITHDRAWAL_ESTIMATE: `${CONSUMER_API_BASE_URL}/withdrawal/estimate`,
  SECURE_WITHDRAWAL: `${CONSUMER_API_BASE_URL}/withdrawal/secure`,
  REQUEST_PAYMENT: `${CONSUMER_API_BASE_URL}/payment/request`,
  CLOSE_CREDITLINE: `${CONSUMER_API_BASE_URL}/close`,
  CREATE_SUNSHINE_JWT: `${CONSUMER_API_BASE_URL}/actions/sunshine-jwt`,
  LOG_EVENT: `${CONSUMER_API_BASE_URL}/log-event`,
  FRONTEND_LOG_MESSAGE: `${CONSUMER_API_BASE_URL}/otel/log-message`,
  STATEMENTS_PERIODS: `${CONSUMER_API_BASE_URL}/statement/periods`,
  STATEMENT_DETAILS: `${CONSUMER_API_BASE_URL}/statement/data`,
  GET_OFFER: `${CONSUMER_API_BASE_URL}/offers`,
  ACCEPT_OFFER: `${CONSUMER_API_BASE_URL}/offers/accept`,
  APPLICATION_STATUS: `${CONSUMER_API_BASE_URL}/application-status`,
  OPT_OUT: `${CONSUMER_API_BASE_URL}/optout`,
  OPT_OUT_CHECK_SURVEY: `${CONSUMER_API_BASE_URL}/optout/check-survey`,
  OPT_OUT_SAVE_SURVEY: `${CONSUMER_API_BASE_URL}/optout/save-survey`,
  INCREASE_CREDIT_LIMIT_AMOUNT: `${CONSUMER_API_BASE_URL}/limit/increase`,
  CREDIT_LINE_SUMMARY: `${CONSUMER_API_BASE_URL}/summary`,
  CREDIT_CHECK_PERMISSION: `${CONSUMER_API_BASE_URL}/credit-pull/get-document`,
  ACCEPT_SIGNATURE: `${CONSUMER_API_BASE_URL}/credit-pull/accept-signature`,
  CONSUMER_SETTINGS: `${CONSUMER_API_BASE_URL}/settings`,
  CONTRACT_DETAILS: `${CONSUMER_API_BASE_URL}/contract/details`,
  FETCH_AVAILABLE_DOCUMENTS: `${CONSUMER_API_BASE_URL}/documents/fetch-available`,
  // PayJoy Card
  PAYJOY_CARD_ACTIVATION: `${CONSUMER_API_BASE_URL}/card/activation`,
  PAYJOY_CARD_DETAILS: `${CONSUMER_API_BASE_URL}/card/details`,
  PAYJOY_CARD_PIN: `${CONSUMER_API_BASE_URL}/card/get-pin`,
  PAYJOY_CARD_SHIPPING: `${CONSUMER_API_BASE_URL}/card/shipping`,
  PAYJOY_CARD_SHIPPING_COVERAGE: `${CONSUMER_API_BASE_URL}/card/shipping/coverage`,
  PAYJOY_CARD_VALIDATE_COVERAGE_ZONE: `${CONSUMER_API_BASE_URL}/card/validate-coverage-zone`,
  PAYJOY_CARD_UPDATE_PIN: `${CONSUMER_API_BASE_URL}/card/changePin`,
  PAYJOY_CARD_TOGGLE_STATUS: `${CONSUMER_API_BASE_URL}/card/toggleStatus`,
  PAYJOY_CARD_DELETE_VIRTUAL: `${CONSUMER_API_BASE_URL}/card/delete/virtual`,
  PAYJOY_CARD_CREATE_VIRTUAL: `${CONSUMER_API_BASE_URL}/card/create/virtual`,
  PAYJOY_CARD_REPORT_NOT_RECEIVED_CARD: `${CONSUMER_API_BASE_URL}/card/mark-undelivered`,
  PAYJOY_CARD_MASTERCARD_BENEFITS: `${CONSUMER_API_BASE_URL}/card/benefits`,
  PAYJOY_CARD_CREATE_CARD_USER: `${CONSUMER_API_BASE_URL}/card/create-card-user`,
  PAYJOY_CARD_DISABLE_CREDIT_CARD: `${CONSUMER_API_BASE_URL}/card/disable`,
  PAYJOY_CARD_REPLACE_CREDIT_CARD: `${CONSUMER_API_BASE_URL}/card/shipping/ship-to-saved-address`,
  PAYJOY_CARD_GET_LATEST_SHIPMENT: `${CONSUMER_API_BASE_URL}/card/shipping/latest-address`,
  PAYJOY_CARD_GET_SHIPMENT_LOGS: `${CONSUMER_API_BASE_URL}/shipments/get-change-logs`,
  // ----- Consumer API migrated endpoints END -----
  BASE: CL_ENDPOINTS_URL,
  NEW_DISCOVERY_EXPERIMENT: `${CL_V1_API_URL}/creditline/experiment/newDiscoveryFlow`,
  CREDIT_LINE_STORE_LOCATOR: `${CONSUMER_HOST}/cl-store-locator`,
  UNLINK_BANK_ACCOUNT: `${CONSUMER_API_HOST}/bankaccount/mx/unlinkAccount`,
  GET_ONLINE_TRANSFER_BANKS: `${CONSUMER_API_HOST}/bankaccount/mx/getAllBanks`,
  VALIDATE_BANK_ACCOUNT: `${CONSUMER_API_HOST}/bankaccount/mx/validate`,
  PRE_APPROVAL_OFFER: `${CL_V1_API_URL}/creditline/preoffer`,
  // KYC validations
  VALIDATIONS_ARE_REQUIRED: `${CONSUMER_API_BASE_URL}/identity-validations/are-required`,
  NATIONAL_ID_VALIDATION: `${CONSUMER_API_BASE_URL}/identity-validations/national-id-validation`,
  // anti money laundering survey
  REQUIRE_AML_SURVEY: `${CONSUMER_API_BASE_URL}/anti-money-laundering/need-survey`,
  SAVE_AML_SURVEY: `${CONSUMER_API_BASE_URL}/anti-money-laundering/save`,
  // In store onboarding
  INIT_PERSON_REG: `${CONSUMER_API_BASE_URL}/instore/pr-init`,
  VALIDATE_PERSON_REG: `${CONSUMER_API_BASE_URL}/instore/pr-validate`,
  GET_IN_STORE_OFFER: `${CONSUMER_API_BASE_URL}/instore/creditline-offer`,
  ACCEPT_IN_STORE_OFFER: `${CONSUMER_API_BASE_URL}/instore/accept-creditline-offer`,
  DECLINE_IN_STORE_OFFER: `${CONSUMER_API_BASE_URL}/instore/decline-creditline-offer`,
  TRIGGER_CREDOLAB_REPORTS_FETCH: `${CONSUMER_API_BASE_URL}/instore/crlb-reports-fetch`,
  // Transaction reports
  REPORT_CARD_TRANSACTION: `${CONSUMER_API_BASE_URL}/card/disputes/report`,
  FETCH_CARD_TRANSACTION_DISPUTES: `${CONSUMER_API_BASE_URL}/card/disputes/fetch`,
  PAYJOY_DASHBOARD_TRANSACTIONS: `${CONSUMER_API_BASE_URL}/dashboard/transactions`,
  PAYJOY_DASHBOARD_UPDATE_SHIPMENT_STATUS: `${CONSUMER_API_BASE_URL}/dashboard/update-shipment-status`,
  PAYJOY_DASHBOARD_DISABLE_CARDS: `${CONSUMER_API_BASE_URL}/dashboard/disable-cards`,
  PAYJOY_DASHBOARD_GET_COUNTRY_SHIPMENT_PROVIDER_CONFIGURATIONS: `${CONSUMER_API_BASE_URL}/dashboard/get-country-shipment-provider-configurations`,
  PAYJOY_DASHBOARD_UPDATE_COUNTRY_SHIPMENT_PROVIDER_CONFIGURATIONS: `${CONSUMER_API_BASE_URL}/dashboard/update-country-shipment-provider-configurations`,
  // Add-On Services
  GET_CART_ADDON: `${CONSUMER_API_BASE_URL}/add-ons`,
  ACTIVATE_CART_ADDON: `${CONSUMER_API_BASE_URL}/add-ons/activate`,
};

const getPathNameFromUrl = (inputUrl: string): string => {
  return new URL(inputUrl).pathname;
};

export { creditLineApiUrls, getPathNameFromUrl };
